import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { cloneDeep, trim } from 'lodash';

import { postClient } from '../../repositories/HttpRepository';
import { ADD_CLIENT_SUCCESS, clientSelector } from '../../reducers/ClientReducer';
import FormatPhoneNumber from '../../utils/FormatPhoneNumber';
import { makeStyles } from 'tss-react/mui';

import ClientInfoForm from './ClientInfo/ClientInfoForm';
import { Backdrop, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

const newClientData = {
  username: '',
  password: '',
  company: '',
  name: '',
  email: '',
  supportEmail: '',
  phone_number: '',
  code: '',
  street: '',
  street2: '',
  city: '',
  state: '',
  zipcode: '',
  timezone: '',
  insuranceEnabled: false,
  insuranceProvider: '',
};

const useStyles = makeStyles()(() => ({
  dialogAction: {
    padding: '8px 24px',
  },
  title: {
    fontWeight: 500,
    fontSize: '1.2rem',
    textTransform: 'uppercase',
  },
  btn: {
    boxShadow: 'none',
    textTransform: 'inherit',
    width: '100px',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  error: {
    fontWeight: 500,
    fontSize: '14px',
  },
  backdrop: {
    position: 'absolute',
    backgroundColor: '#fff',
    zIndex: 9999,
  },
}));

const CreateClient = () => {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const updateValues = useSetRecoilState(clientSelector);
  const [client, setClient] = useState(newClientData);
  const [error, setError] = useState('');
  const [isSubmit, setIsSubmit] = useState(false);
  const formRef = useRef();

  const submitForm = useCallback(() => {
    if (isSubmit) return;

    //call validate function in ClientInfoForm
    const errors = formRef.current.validateForm();
    if (errors) {
      return;
    }

    let params = cloneDeep(client);
    params.phone_number = FormatPhoneNumber(params.phone_number);

    for (const key in params) {
      if (typeof params[key] === 'string') params[key] = trim(params[key]);
    }

    params = {
      ...params,
      address: {
        street: params.street,
        street2: params.street2,
        city: params.city,
        state: params.state,
        zipcode: params.zipcode,
      },
    };

    setIsSubmit(true);
    postClient(params)
      .then((res) => {
        if (res.ok) {
          updateValues({
            type: ADD_CLIENT_SUCCESS,
            data: res.data,
          });
          navigate('/');
        } else if (res?.data?.errors) {
          setError(res.data.errors[0]);
        } else {
          setError('Server is having problem, please try again later.');
        }
      })
      .finally(() => {
        setIsSubmit(false);
      });
  }, [client, isSubmit]);

  const onClose = () => {
    navigate('/');
  };
  const dialogActions = [
    <Button key={`cancel-button`} className={classes.btn} onClick={onClose} variant="contained">
      Cancel
    </Button>,
    <Button key={`add-button`} className={classes.btn} onClick={submitForm} variant="contained" color="primary">
      Add
    </Button>,
  ];

  return (
    <Dialog open onClose={onClose} aria-labelledby="form-dialog-title" maxWidth="md">
      <DialogTitle id="form-dialog-title">
        <Typography className={classes.title}>Add New Client</Typography>
      </DialogTitle>
      <DialogContent>
        <Backdrop open={isSubmit} className={classes.backdrop}>
          <CircularProgress />
        </Backdrop>
        <ClientInfoForm title="Add New Client" initialData={client} onClose={onClose} setError={setError} error={error} isSubmit={isSubmit} handleDataChange={setClient} ref={formRef} />
        <Typography color="error" variant="caption" className={classes.error}>
          {error}
        </Typography>

        <DialogActions className={classes.dialogAction}>
          {dialogActions.map((action) => {
            return action;
          })}
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(CreateClient);
